// Generic directional swipe container: renders whatever is passed
// as `children` for the current `activeKey`, and animates between
// keys as a full carousel-style page swap — the outgoing content
// slides off in `direction` while the incoming content slides in
// from the opposite side. Not tied to the marketplace; anything
// that needs "these are pages, not a scroll" can reuse this.
//
// Direction is supplied by the caller (rather than computed here)
// since only the caller knows the semantic order of its own keys.

import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import styles from './SwipeStage.module.scss';

export interface SwipeStageProps {
  /** Unique key for the content currently being shown — changing it triggers the transition. */
  activeKey: string;
  /** 1 = moving forward (new content enters from the right), -1 = moving backward (enters from the left). */
  direction: 1 | -1;
  children: ReactNode;
}

export function SwipeStage({ activeKey, direction, children }: SwipeStageProps) {
  const reducedMotion = useReducedMotion();

  const variants = {
    enter: (dir: 1 | -1) => ({
      x: reducedMotion ? 0 : dir > 0 ? '100%' : '-100%',
      opacity: reducedMotion ? 1 : 0,
    }),
    center: { x: '0%', opacity: 1 },
    exit: (dir: 1 | -1) => ({
      x: reducedMotion ? 0 : dir > 0 ? '-100%' : '100%',
      opacity: reducedMotion ? 1 : 0,
    }),
  };

  const transition = reducedMotion
    ? { duration: 0.12, ease: 'easeOut' as const }
    : { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.9 };

  return (
    <div className={styles.stage}>
      <AnimatePresence mode="popLayout" initial={false} custom={direction}>
        <motion.div
          key={activeKey}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={transition}
          className={styles.slide}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
